import { useMemo, useState, useEffect } from "react";
import {
  Area,
  AreaChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";
import { DONUT_COLORS } from "@/lib/dashboard/chartColors";
import { useI18n } from "@/hooks/useI18n";
import { useAuth } from "@/hooks/useAuth";
import { useAgencyDashboardStats } from "@/hooks/useAgencyDashboardStats";
import { AgencyDashboardPeriod } from "@/integrations/supabase/rpc-types";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Briefcase,
  CheckCircle2,
  MapPin,
  Receipt,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
  XCircle,
} from "lucide-react";

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function formatPercent(value: number): string {
  return `${(value || 0).toLocaleString("it-IT", { maximumFractionDigits: 1 })}%`;
}

interface KpiCardProps {
  icon: React.ComponentType<{ className?: string }>;
  iconClassName: string;
  label: string;
  value: string;
  info: string;
}

function KpiCard({ icon: Icon, iconClassName, label, value, info }: KpiCardProps) {
  return (
    <Card>
      <CardContent className="p-4 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className={`flex items-center justify-center h-8 w-8 rounded-lg flex-shrink-0 ${iconClassName}`}>
            <Icon className="h-4 w-4" />
          </div>
          <span className="text-sm text-gray-500">{label}</span>
          <InfoTooltip text={info} />
        </div>
        <span className="text-xl font-semibold text-gray-900">{value}</span>
      </CardContent>
    </Card>
  );
}

interface AgencyOverviewTabProps {
  period: AgencyDashboardPeriod;
}

export function AgencyOverviewTab({ period }: AgencyOverviewTabProps) {
  const { t } = useI18n();
  const { userRole } = useAuth();
  const { data, loading, error, refresh } = useAgencyDashboardStats(period);
  const showTeamSize = userRole !== "travel_designer" && userRole !== "travel_advisor_member";

  const [minLoadingDone, setMinLoadingDone] = useState(false);
  useEffect(() => {
    setMinLoadingDone(false);
    const timer = setTimeout(() => setMinLoadingDone(true), 500);
    return () => clearTimeout(timer);
  }, [period]);

  const isLoading = loading || !minLoadingDone;

  const chartData = useMemo(
    () =>
      (data?.timeseries.current ?? []).map((point) => ({
        date: point.date,
        value: point.value,
        delta: point.delta,
      })),
    [data]
  );

  const donutTotal = useMemo(
    () => (data?.destinations_donut ?? []).reduce((sum, d) => sum + d.count, 0),
    [data]
  );

  if (isLoading) {
    const kpiCount = showTeamSize ? 7 : 6;
    return (
      <div className="space-y-4">
        {/* KPI grid skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {Array.from({ length: kpiCount }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-8 w-8 rounded-lg flex-shrink-0" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <Skeleton className="h-7 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Chart + donut skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          <Card className="lg:col-span-1 xl:col-span-2">
            <CardHeader className="pb-2">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-4 w-32 mt-1" />
            </CardHeader>
            <CardContent className="h-64">
              <Skeleton className="h-full w-full rounded-lg" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <Skeleton className="h-5 w-36" />
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Skeleton className="h-40 w-40 rounded-full flex-shrink-0" />
                <div className="space-y-2 flex-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-3.5 w-full" />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Secondary metrics skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Skeleton className="h-9 w-9 rounded-lg flex-shrink-0" />
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-6 w-12" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent>
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-50 text-red-500">
              <XCircle className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-900">
                {t("dashboard.overview.errorTitle")}
              </p>
              <p className="text-sm text-gray-500">
                {t("dashboard.overview.errorDescription")}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => refresh()} className="mt-1">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t("dashboard.retry")}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { kpis, timeseries, destinations_donut: destinations } = data;
  const deltaPositive = (timeseries.delta_percent ?? 0) >= 0;

  return (
    <div className="space-y-4">
      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard
          icon={Briefcase}
          iconClassName="bg-amber-50 text-amber-600"
          label={t("dashboard.overview.activeCount")}
          value={String(kpis.active_count)}
          info={t("dashboard.overview.activeCountInfo")}
        />
        <KpiCard
          icon={CheckCircle2}
          iconClassName="bg-emerald-50 text-emerald-600"
          label={t("dashboard.overview.completedCount")}
          value={String(kpis.completed_count)}
          info={t("dashboard.overview.completedCountInfo")}
        />
        <KpiCard
          icon={Wallet}
          iconClassName="bg-blue-50 text-blue-600"
          label={t("dashboard.overview.closedValueTotal")}
          value={formatCurrency(kpis.closed_value_total)}
          info={t("dashboard.overview.closedValueTotalInfo")}
        />
        <KpiCard
          icon={TrendingUp}
          iconClassName="bg-orange-50 text-orange-600"
          label={t("dashboard.overview.marginTotal")}
          value={formatCurrency(kpis.net_earnings_total)}
          info={t("dashboard.overview.marginTotalInfo")}
        />
        {kpis.platform_fee_total != null && (
          <KpiCard
            icon={Receipt}
            iconClassName="bg-purple-50 text-purple-600"
            label={t("dashboard.overview.platformFeeTotal")}
            value={formatCurrency(kpis.platform_fee_total)}
            info={t("dashboard.overview.platformFeeTotalInfo")}
          />
        )}
        <KpiCard
          icon={Receipt}
          iconClassName="bg-gray-100 text-gray-600"
          label={t("dashboard.overview.avgMarginPercent")}
          value={formatPercent(kpis.avg_margin_percent)}
          info={t("dashboard.overview.avgMarginPercentInfo")}
        />
        <KpiCard
          icon={Wallet}
          iconClassName="bg-gray-100 text-gray-600"
          label={t("dashboard.overview.avgValuePerItinerary")}
          value={formatCurrency(kpis.avg_value_per_itinerary)}
          info={t("dashboard.overview.avgValuePerItineraryInfo")}
        />
        {showTeamSize && (
          <KpiCard
            icon={Users}
            iconClassName="bg-gray-100 text-gray-600"
            label={t("dashboard.overview.teamSize")}
            value={String(kpis.team_size)}
            info={t("dashboard.overview.teamSizeInfo")}
          />
        )}
      </div>

      {/* Chart + donut */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        <Card className="lg:col-span-1 xl:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              {t("dashboard.overview.trendTitle")}
              <InfoTooltip text={t("dashboard.overview.trendTitleInfo")} />
            </CardTitle>
            <div className="flex items-center gap-2 text-sm">
              <span className={deltaPositive ? "text-emerald-600" : "text-red-500"}>
                {deltaPositive ? (
                  <TrendingUp className="inline h-4 w-4 mr-1" />
                ) : (
                  <TrendingDown className="inline h-4 w-4 mr-1" />
                )}
                {timeseries.delta_amount >= 0 ? "+" : ""}
                {formatCurrency(timeseries.delta_amount)}
                {timeseries.delta_percent != null && ` (${formatPercent(timeseries.delta_percent)})`}
              </span>
              <span className="text-gray-400">
                · {t("dashboard.overview.periodDays", { days: String(timeseries.current.length ? data.meta.period_days : 0) })}
              </span>
            </div>
          </CardHeader>
          <CardContent className="h-64">
            {chartData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-sm text-gray-400">
                {t("dashboard.overview.noTrendData")}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="agencyTrendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f97316" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(d: string) => new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}
                  />
                  <RechartsTooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const point = payload[0].payload as { value: number; delta: number };
                      return (
                        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 text-sm space-y-1">
                          <p className="text-gray-500">
                            {new Date(label as string).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
                          </p>
                          <p className="font-semibold text-gray-900">{t("dashboard.overview.tooltipTotal")} {formatCurrency(point.value)}</p>
                          <p className="text-emerald-600 text-xs">+{formatCurrency(point.delta)} {t("dashboard.overview.tooltipDelta")}</p>
                        </div>
                      );
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#f97316"
                    strokeWidth={2}
                    fill="url(#agencyTrendFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              {t("dashboard.overview.destinationsTitle")}
              <InfoTooltip text={t("dashboard.overview.destinationsTitleInfo")} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {destinations.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-sm text-gray-400">
                {t("dashboard.overview.noDestinationsData")}
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="h-40 w-40 flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={destinations}
                        dataKey="count"
                        nameKey="destination"
                        innerRadius={45}
                        outerRadius={70}
                        paddingAngle={2}
                      >
                        {destinations.map((entry, index) => (
                          <Cell key={entry.destination} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value: number) => `${value} (${((value / (donutTotal || 1)) * 100).toFixed(0)}%)`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="space-y-1.5 text-sm flex-1 min-w-0">
                  {destinations.map((d, index) => (
                    <li key={d.destination} className="flex items-center gap-2 min-w-0">
                      <span
                        className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: DONUT_COLORS[index % DONUT_COLORS.length] }}
                      />
                      <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                      <span className="truncate text-gray-700">{d.destination}</span>
                      <span className="ml-auto text-gray-500 flex-shrink-0">{formatPercent(d.percentage)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Secondary metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-gray-100 text-gray-600 flex-shrink-0">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm text-gray-500 flex items-center gap-1.5">
                {t("dashboard.overview.connectedClients")}
                <InfoTooltip text={t("dashboard.overview.connectedClientsInfo")} />
              </div>
              <div className="text-lg font-semibold text-gray-900">{kpis.connected_clients}</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AgencyOverviewTab;
